import { validations } from "@/utils/validationUtils";
import { Button, Descriptions, Flex, Form, Modal, Popover, Radio } from "antd";
import { DescriptionsItemType } from "antd/es/descriptions";
import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEnumeration, useGetAgentEnumerations } from "../../../../common/api/queries";
import HiddenInput from "../../../../common/components/form/components/HiddenInput";
import AntIcon from "../../../../common/components/icons/AntIcon";
import { ModalSizes } from "../../../../common/constants";
import { UUID } from "../../../../typings/global";
import { formatAgentDirectorateName, formatAgentName } from "../../../agent/utils";
import { AgentDirectoratesSelect } from "../../../enumerations/components/form/AgentDirectoratesSelect";
import { AgentSelect } from "../../../enumerations/components/form/AgentSelect";
import { EnumerationType } from "../../../enumerations/enums";
import { ContractFilterPageRequest } from "../../types";

enum AGENT_FILTER_TYPE {
  DIRECT_SUBORDINATES,
  ALL_SUBORDINATES,
  DIRECTORATES,
}

type FieldNames = {
  directSubordinateIdFieldName: keyof ContractFilterPageRequest;
  allSubordinateIdFieldName: keyof ContractFilterPageRequest;
  directorateIdsFieldName: keyof ContractFilterPageRequest;
};

type RenderItemProps = {
  fieldNames: FieldNames;
  filterType: AGENT_FILTER_TYPE;
};

const renderFormItem = ({ fieldNames, filterType }: RenderItemProps) => {
  switch (filterType) {
    case AGENT_FILTER_TYPE.DIRECT_SUBORDINATES:
      return (
        <Form.Item name={fieldNames.directSubordinateIdFieldName} label={t("common.agent")} rules={[validations.none]}>
          <AgentSelect allowClear />
        </Form.Item>
      );
    case AGENT_FILTER_TYPE.ALL_SUBORDINATES:
      return (
        <Form.Item name={fieldNames.allSubordinateIdFieldName} label={t("common.agent")} rules={[validations.none]}>
          <AgentSelect allowClear />
        </Form.Item>
      );
    default:
      return (
        <Form.Item
          name={fieldNames.directorateIdsFieldName}
          label={t("agent.filter.directorates")}
          rules={[validations.none]}
        >
          <AgentDirectoratesSelect style={{ width: "100%" }} isMultiple allowClear />
        </Form.Item>
      );
  }
};

type Props = {
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: (values: Record<string, string | string[] | undefined>) => void;
  fieldNames: FieldNames;
};

export const AgentExtendedFilterModal = ({ isOpen, onCancel, fieldNames, onSubmit }: Props) => {
  const { t } = useTranslation();

  const directoratesEnums = useEnumeration(EnumerationType.AGENT_DIRECTORATES);

  const [agentFilterType, setAgentFilterType] = useState<AGENT_FILTER_TYPE>(AGENT_FILTER_TYPE.DIRECT_SUBORDINATES);

  const parentForm = Form.useFormInstance<ContractFilterPageRequest>();
  const parentFormValues = Form.useWatch([], { form: parentForm, preserve: true }) as
    ContractFilterPageRequest | undefined;

  const directorateIds = parentFormValues?.[fieldNames.directorateIdsFieldName] as UUID[] | undefined;
  const directSubordinateId = parentFormValues?.[fieldNames.directSubordinateIdFieldName] as UUID | undefined;
  const allSubordinateId = parentFormValues?.[fieldNames.allSubordinateIdFieldName] as UUID | undefined;
  const agentIds = [directSubordinateId, allSubordinateId].filter((id) => id !== undefined);

  const { data: agentsData } = useGetAgentEnumerations(agentIds);

  const [form] = Form.useForm<Record<string, string | string[] | undefined>>();

  useEffect(() => {
    if (isOpen && parentFormValues) {
      form.setFieldsValue({
        [fieldNames.allSubordinateIdFieldName]: allSubordinateId,
        [fieldNames.directSubordinateIdFieldName]: directSubordinateId,
        [fieldNames.directorateIdsFieldName]: directorateIds,
      });
    }
  }, [isOpen, parentFormValues]);

  const formattedFilterResults: DescriptionsItemType[] = useMemo(() => {
    const result: DescriptionsItemType[] = [];

    if (directorateIds?.length) {
      const data: string[] = [];

      directorateIds.forEach((directorateId) => {
        const directorate = directoratesEnums.find((item) => item.id === directorateId);

        data.push(directorate ? formatAgentDirectorateName(directorate) : "");
      });

      result.push({
        key: "directorateIds",
        label: t("agent.filter.directorates"),
        children: data.join(", "),
      });
    }

    if (directSubordinateId) {
      const directSubordinateOfAgent = agentsData?.find((agent) => agent.id === directSubordinateId);

      result.push({
        key: "directSubordinateId",
        label: t("agent.filter.directSubordinatesOfAgent"),
        children: directSubordinateOfAgent ? formatAgentName(directSubordinateOfAgent) : undefined,
      });
    }

    if (allSubordinateId) {
      const allSubordinateOfAgent = agentsData?.find((agent) => agent.id === allSubordinateId);

      result.push({
        key: "allSubordinateId",
        label: t("agent.filter.allSubordinatesOfAgent"),
        children: allSubordinateOfAgent ? formatAgentName(allSubordinateOfAgent) : undefined,
      });
    }

    return result;
  }, [agentsData, directorateIds, directSubordinateId, allSubordinateId]);

  return (
    <>
      <HiddenInput name={fieldNames.directorateIdsFieldName} />
      <HiddenInput name={fieldNames.allSubordinateIdFieldName} />
      <HiddenInput name={fieldNames.directSubordinateIdFieldName} />

      <Modal
        open={isOpen}
        onCancel={onCancel}
        width={ModalSizes.SMALL}
        title={t("agent.filter.extendedFilter")}
        okText={t("common.select")}
        cancelText={t("common.cancel")}
        onOk={() =>
          form.validateFields().then((values) =>
            onSubmit({
              [fieldNames.allSubordinateIdFieldName]: values[fieldNames.allSubordinateIdFieldName],
              [fieldNames.directSubordinateIdFieldName]: values[fieldNames.directSubordinateIdFieldName],
              [fieldNames.directorateIdsFieldName]: values[fieldNames.directorateIdsFieldName],
            }),
          )
        }
        afterClose={() => form.resetFields()}
        mask={{ closable: false }}
      >
        <Radio.Group
          className="margin-bottom-medium margin-top-medium"
          style={{ display: "flex", justifyContent: "center" }}
          value={agentFilterType}
          onChange={(event) => {
            const filterType = event.target.value as AGENT_FILTER_TYPE;
            setAgentFilterType(filterType);
          }}
          size="large"
        >
          <Radio.Button value={AGENT_FILTER_TYPE.DIRECT_SUBORDINATES}>
            {t("agent.filter.directSubordinates")}
          </Radio.Button>
          <Radio.Button value={AGENT_FILTER_TYPE.ALL_SUBORDINATES}>{t("agent.filter.allSubordinates")}</Radio.Button>
          <Radio.Button value={AGENT_FILTER_TYPE.DIRECTORATES}>{t("agent.filter.directorates")}</Radio.Button>
        </Radio.Group>

        <Form form={form} layout="vertical" name={`agentExtendedFilter-${fieldNames.directorateIdsFieldName}`}>
          {renderFormItem({ fieldNames, filterType: agentFilterType })}
        </Form>
      </Modal>

      {formattedFilterResults.length ? (
        <div style={{ position: "absolute", left: 0, top: 66 }}>
          <Flex align="center" style={{ height: 20 }}>
            <Popover
              placement="bottom"
              title={t("contract.filter.activeFilter")}
              content={<Descriptions bordered size="small" column={1} items={formattedFilterResults} />}
            >
              <span className="sub-header-info cursor-pointer">
                {t("components.agentSelect.extendedFilterActive")} <AntIcon type="question" />
              </span>
            </Popover>

            <Button
              size="small"
              style={{ marginLeft: 4 }}
              variant="text"
              color="danger"
              icon={<AntIcon type="delete" style={{ marginRight: -4 }} />}
              onClick={() => {
                const data = {
                  [fieldNames.directSubordinateIdFieldName]: undefined,
                  [fieldNames.allSubordinateIdFieldName]: undefined,
                  [fieldNames.directorateIdsFieldName]: undefined,
                };

                onSubmit(data);
              }}
            >
              {t("common.cancel")}
            </Button>
          </Flex>
        </div>
      ) : undefined}
    </>
  );
};
